import React from 'react';
import usePaginatedData from '../hooks/usePaginatedData';
import useDataCache from '../hooks/useDataCache';

/**
 * Higher-Order Component that adds pagination and caching to any admin page
 * 
 * Usage:
 * export default withOptimization(YourComponent, {
 *   collection: 'tests',
 *   pageSize: 20,
 *   cacheTTL: 5 * 60 * 1000
 * });
 */
export const withOptimization = (WrappedComponent, options = {}) => {
  const {
    collection = null,
    pageSize = 20,
    cacheTTL = 5 * 60 * 1000,
    filters = {},
    orderByField = 'createdAt',
    orderDirection = 'desc'
  } = options;

  return function OptimizedComponent(props) {
    // Always call hooks unconditionally (React rules)
    const paginatedData = usePaginatedData(
      collection || 'dummy', // Provide dummy collection if none specified
      filters,
      pageSize,
      orderByField,
      orderDirection
    );

    // Always provide cache
    const cache = useDataCache(cacheTTL);

    // Merge optimizations with original props
    const enhancedProps = {
      ...props,
      // Only include pagination if collection was provided
      ...(collection && {
        data: paginatedData.data,
        loading: paginatedData.loading,
        hasMore: paginatedData.hasMore,
        loadMore: paginatedData.loadMore,
        refresh: paginatedData.refresh,
        page: paginatedData.page
      }),
      // Caching
      cache,
      // Performance helpers
      optimized: true
    };

    return <WrappedComponent {...enhancedProps} />;
  };
};

// Also export as default
export default withOptimization;

/**
 * Preset configurations for common admin pages
 */
export const optimizationPresets = {
  tests: {
    collection: 'tests',
    pageSize: 20,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  },
  users: {
    collection: 'users',
    pageSize: 50,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  },
  resources: {
    collection: 'resources',
    pageSize: 20,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  },
  batches: {
    collection: 'batches',
    pageSize: 20,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  },
  payments: {
    collection: 'feePayments',
    pageSize: 50,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  },
  attendance: {
    collection: 'attendance',
    pageSize: 30,
    orderByField: 'date',
    orderDirection: 'desc'
  },
  courses: {
    collection: 'courses',
    pageSize: 20,
    orderByField: 'createdAt',
    orderDirection: 'desc'
  }
};

/**
 * Quick wrapper function using presets
 * 
 * Usage:
 * export default withOptimizedPreset(TestManagement, 'tests');
 */
export const withOptimizedPreset = (Component, presetName) => {
  const preset = optimizationPresets[presetName];
  if (!preset) {
    console.warn(`Preset "${presetName}" not found. Using default optimization.`);
    return withOptimization(Component);
  }
  return withOptimization(Component, preset);
};
